import { AfterViewInit, Directive, ElementRef, HostListener, NgZone, OnDestroy } from '@angular/core';

/**
 * Horizontal scroll affordance for a scrollable element. Toggles
 * `bk-scroll--more-left` / `bk-scroll--more-right` classes by scroll position so
 * CSS can fade the edge that still has hidden content (the common "more columns
 * offscreen" cue). Re-checks on scroll, on window resize, and when the contents
 * change (e.g. a DataTable redraw or an async data load).
 *
 * Usage: `<div class="...scroll" bkScrollShadow><table>...</table></div>`
 * The host needs `overflow-x: auto`; the consuming CSS draws the fades from the
 * two state classes.
 */
@Directive({
  selector: '[bkScrollShadow]',
})
export class BkScrollShadowDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;

  constructor(private readonly host: ElementRef<HTMLElement>, private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    this.update();
    // Content can arrive async (data load) or be re-rendered (DataTables draw);
    // watch for it outside Angular so we do not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.update());
      this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  @HostListener('scroll')
  @HostListener('window:resize')
  update(): void {
    const el = this.host.nativeElement;
    const max = el.scrollWidth - el.clientWidth;
    const overflowing = max > 1;
    el.classList.toggle('bk-scroll--more-left', overflowing && el.scrollLeft > 1);
    el.classList.toggle('bk-scroll--more-right', overflowing && el.scrollLeft < max - 1);
  }
}
